import { ErrorDiagnostics } from "./error-diagnostics";
import { redactEmailTrackingUrls } from "./email-attribution";
import { trackMetaProductEvent } from "@/lib/meta-pixel";
import posthog, { type CaptureResult } from "posthog-js";
import type { StarterCustomizationPayload } from "./starter-customization";
import type { LengthValuePayload, LengthValueEligibility } from "./length-value";
import type { PaywallTrigger } from "./projects";
import type { AgentDraftMeasurement } from "./agent-tools/measurement";
import type { EditorEntryArtifact } from "./editor-entry-artifact";
import { isSearchProjectTheme } from "./search-project-themes";
import { isValidAnalyticsUserAgent } from "./analytics-user-agent";
import type { AssistantClientFailureEvent } from "./assistant/client-telemetry";
import type {
  EquipmentCapVariant,
  EquipmentExemption,
  EquipmentLimitOperation,
  EquipmentMutationSource,
} from "./equipment-limit";

const KEY = import.meta.env.VITE_POSTHOG_KEY;
const HOST = import.meta.env.VITE_POSTHOG_HOST ?? "https://eu.i.posthog.com";

type Product = "lifetime" | "installation_pack" | "monthly_subscription" | "pro_monthly" | "pro_yearly" | "ai_credits" | "pass";

export type CloudProjectCreationSource =
  | "desktop_save"
  | "desktop_nudge"
  | "desktop_share"
  | "desktop_save_as"
  | "desktop_duplicate"
  | "mobile_save"
  | "mobile_share"
  | "share_view_copy"
  | "public_view_copy";

export type CloudProjectAccessMode =
  | "free"
  | "lifetime"
  | "legacy"
  | "unmetered";

/** Events owned by the local-project and paid-action funnels. Existing
 * product, export, pricing-interest and AI telemetry continues through
 * `track`, so there is one PostHog capture per call site. */
export type AnalyticsEvents = {
  assistant_client_request_failed: AssistantClientFailureEvent;
  offer_experiment_assigned: {
    experiment_key: string; experiment_version: string; assignment_id: string;
    assigned_at: string; variant: EquipmentCapVariant;
    "$feature/equipment-cap-free-build-v1": EquipmentCapVariant;
  };
  offer_experiment_exposed: {
    experiment_key: string; experiment_version: string; assignment_id: string;
    variant: EquipmentCapVariant; surface: string;
  };
  offer_assignment_conflict: { experiment_key: string; experiment_version: string; assignment_id: string };
  equipment_cap_experiment_exposed: { experiment_key: string; variant: EquipmentCapVariant };
  agent_draft_opened: AgentDraftMeasurement & { status: "valid" | "invalid" };
  agent_draft_reviewed: AgentDraftMeasurement & { equipment_count: number; cable_count: number; assumption_count: number; finding_count: number };
  agent_draft_imported: AgentDraftMeasurement & { equipment_count: number; cable_count: number; assumption_count: number; finding_count: number };
  meaningful_project_value_reached: LengthValuePayload;
  meaningful_project_value_eligibility: LengthValueEligibility;
  starter_customization_saved: StarterCustomizationPayload;
  starter_opened: Pick<StarterCustomizationPayload, "definition_version" | "starter_source" | "search_project_theme">;
  /** The editor booted for a person. Fired from the destination so the
   * landing → canvas hop is counted even when the CTA's own event was lost to
   * the navigation; `entry_source` is the CTA when we saw the click. */
  app_entered: EditorEntryArtifact & {
    entry_source: string;
    entry_from_path: string | null;
    entry_referrer_domain: string | null;
    arrival_surface: "share" | "public" | "owner" | "project" | "cold";
    returning_visitor: boolean;
    opens_with_component: boolean;
    /** The catalog id in `?component=` / `?starter=`: which SKU page sent them. */
    entry_component: string | null;
  };
  first_value_reached: {
    first_value_action: "ai_system" | "ai_component" | "canvas";
    first_value_was_ai: boolean;
    arrival_surface: "share" | "public" | "owner" | "project" | "cold";
    seconds_to_first_value: number;
  };
  local_project_save_succeeded: { local_project_id: string };
  local_project_save_failed: { error_kind: string };
  paywall_shown: {
    trigger: PaywallTrigger;
    authenticated: boolean;
    value_moment_reached: boolean;
    checkout_attempt_id: string;
  };
  equipment_count_changed: EquipmentLimitAnalyticsContext & {
    mutation_id: string;
    operation: EquipmentLimitOperation;
    mutation_source: EquipmentMutationSource;
    previous_equipment_count: number;
    equipment_delta: number;
    successful: true;
  };
  equipment_limit_milestone_reached: EquipmentLimitAnalyticsContext & {
    mutation_id: string;
    mutation_source: EquipmentMutationSource;
    milestone: 5 | 8 | 10;
    crossed_by_delta: number;
    $insert_id: string;
  };
  equipment_limit_exempt_item_added: EquipmentLimitAnalyticsContext & {
    mutation_id: string;
    mutation_source: EquipmentMutationSource;
    item_kind: EquipmentExemption | "wire";
    added_count: number;
    at_limit: boolean;
  };
  equipment_limit_blocked: EquipmentLimitAnalyticsContext & {
    mutation_id: string;
    checkout_attempt_id: string;
    operation: EquipmentLimitOperation;
    mutation_source: EquipmentMutationSource;
    attempted_equipment_delta: number;
    would_be_equipment_count: number;
    attempted_category:
      | "battery"
      | "charger"
      | "solar"
      | "source"
      | "engine"
      | "distribution"
      | "load"
      | "monitor"
      | "unknown";
    value_moment_reached: boolean;
  };
  equipment_limit_paywall_dismissed: EquipmentLimitAnalyticsContext & {
    checkout_attempt_id: string;
    seconds_visible: number;
  };
  equipment_project_restarted: EquipmentLimitAnalyticsContext & {
    restart_source: "new_project";
    previous_equipment_count: number;
    after_limit_hit: boolean;
    checkout_attempt_id: string | null;
    seconds_since_limit_hit: number | null;
  };
  cloud_nudge_shown: {
    trigger: "local_save";
    authenticated: boolean;
    node_count: number;
    cable_count: number;
  };
  cloud_nudge_clicked: {
    trigger: "local_save";
    authenticated: boolean;
    node_count: number;
    cable_count: number;
  };
  cloud_nudge_dismissed: {
    trigger: "local_save";
    authenticated: boolean;
    node_count: number;
    cable_count: number;
  };
  mobile_cloud_projects_opened: {
    entry_point: "welcome" | "menu" | "title";
    authenticated: boolean;
    has_current_project: boolean;
  };
  mobile_cloud_project_opened: {
    entry_point: "welcome" | "menu" | "title";
    project_id: string;
    node_count: number;
    cable_count: number;
  };
  pricing_cta_clicked: {
    trigger: PaywallTrigger;
    product: Product;
    authenticated: boolean;
    checkout_attempt_id: string;
  };
  checkout_started: {
    trigger: PaywallTrigger;
    product: Product;
    checkout_attempt_id: string;
    transaction_id: string;
  };
  checkout_preparation_started: {
    trigger: PaywallTrigger;
    product: Product;
    checkout_attempt_id: string;
  };
  checkout_preparation_failed: {
    trigger: PaywallTrigger;
    product: Product;
    checkout_attempt_id: string;
    stage: "create_transaction" | "initialize_paddle" | "open_checkout";
  };
  checkout_ui_completed: {
    trigger: PaywallTrigger;
    product: Product;
    checkout_attempt_id: string;
    transaction_id: string;
    verified: false;
  };
  project_created: {
    project_id: string;
    node_count: number;
    cable_count: number;
    source: CloudProjectCreationSource;
    access_mode: CloudProjectAccessMode;
    claimed_free_slot: boolean;
    free_slot_claim_number: number | null;
    free_slot_claim_reason: "backfill" | "create" | "update_reclaim" | null;
    free_slot_recreated: boolean;
    has_lifetime_access: boolean;
    has_legacy_access: boolean;
    monetization_enabled: boolean;
  };
  cloud_project_deleted: {
    project_id: string;
    source: "desktop_project_picker";
    was_current_project: boolean;
    released_free_slot: boolean;
    free_slot_claim_number: number | null;
    has_lifetime_access: boolean;
    has_legacy_access: boolean;
    monetization_enabled: boolean;
  };
  cloud_save_attempted: { authenticated: boolean; entitled: boolean };
  export_attempted: {
    entitled: boolean;
    value_moment_reached: boolean;
    node_count: number;
    cable_count: number;
    format: "12v-json" | "device-pdf" | "canvas-png" | "blueprint";
  };
  /** The canonical JSON/PDF/PNG completion event is `system_exported`.
   * This typed milestone is blueprint-only to avoid double-counting. */
  export_completed: { format: "blueprint-print" };
};

export type EquipmentLimitAnalyticsContext = {
  count_policy_version: "equipment-v1";
  equipment_limit: number;
  equipment_count: number;
  remaining_equipment: number;
  authenticated: boolean;
  entitled: boolean;
  limit_enforced: boolean;
  monetization_enabled: boolean;
  equipment_limit_journey_id: string;
};

const errorDiagnostics = new ErrorDiagnostics();

let initialised = false;
let identifiedDistinctId: string | null = null;

export function isAnalyticsInitialized(): boolean {
  return initialised;
}

const IGNORED_EXCEPTION_PATTERNS = [
  /^\[vite\]/,
  /Failed to reload/,
  /Error invoking postEvent/,
  /initEternlDomAPI/,
  /WKWebView API client did not respond/,
  /Failed to read the 'cssRules' property/,
  /navigationPerformanceLogger/,
  /webkit\.messageHandlers\.\w+\.postMessage/,
  /^\[React Flow\]/,
];

function suppressKnownException(event: CaptureResult | null): CaptureResult | null {
  if (!event || event.event !== "$exception") return event;
  const exceptions = event.properties.$exception_list;
  // Android in-app browsers inject this performance logger. Its Java bridge
  // can disappear during navigation; no Crimp code owns or can repair it.
  // Match the original SDK frames, before PostHog's server-side symbolication.
  // Keep incomplete stacks and mixed exception chains so app errors stay visible.
  if (
    event.properties.$os === "Android" &&
    Array.isArray(exceptions) &&
    exceptions.length > 0 &&
    exceptions.every((entry: unknown) => {
      if (!entry || typeof entry !== "object") return false;
      const exception = entry as { value?: unknown; stacktrace?: { frames?: unknown } };
      if (exception.value !== "Error invoking postMessage: Java object is gone") return false;
      const frames = exception.stacktrace?.frames;
      if (!Array.isArray(frames) || frames.length === 0) return false;
      return frames.every((frame: unknown) => {
        if (!frame || typeof frame !== "object") return false;
        const { filename, in_app } = frame as { filename?: unknown; in_app?: unknown };
        return filename === "iabjs://navigation_performance_logger_android" && in_app !== true;
      }) && frames.some((frame: { function?: unknown }) => frame.function === "sendDataToNative");
    })
  ) return null;
  const message = Array.isArray(exceptions)
    ? exceptions
      .map((entry) => {
        if (!entry || typeof entry !== "object") return "";
        const value = (entry as { value?: unknown }).value;
        const type = (entry as { type?: unknown }).type;
        return `${typeof type === "string" ? type : ""} ${typeof value === "string" ? value : ""}`;
      })
      .join(" ")
    : "";
  return IGNORED_EXCEPTION_PATTERNS.some((pattern) => pattern.test(message))
    ? null
    : event;
}

/** Initialise synchronously so boot-time super-properties are registered on
 * the first page view. The resolved promise keeps compatibility with callers
 * that need to sequence work after initialization. */
export function initAnalytics(): Promise<void> {
  // Handover fragments contain bearer credentials; recipients are not tracked.
  if (typeof window !== "undefined" && /^\/handover\/?$/.test(window.location.pathname)) return Promise.resolve();
  if (!initialised && KEY) {
    posthog.init(KEY, {
      api_host: HOST,
      person_profiles: "identified_only",
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      // A visible experiment CTA can be encountered in a later visit. Keep
      // native exposure deduplication within that visit, not browser lifetime.
      advanced_feature_flags_dedup_per_session: true,
      // React Flow's pane/edge/handle surfaces treat rapid pointer sequences as
      // normal gestures (pan, wire, select, resize). PostHog otherwise reports
      // those as rage clicks, usually without element text. Keep actual editor
      // controls eligible so a broken button can still surface as friction.
      rageclick: {
        css_selector_ignorelist: [
          ".ph-no-rageclick",
          ".react-flow__pane",
          ".react-flow__edge",
          ".react-flow__handle",
          ".react-flow__resize-control",
        ],
      },
      mask_all_text: true,
      disable_session_recording: import.meta.env.DEV,
      session_recording: {
        sampleRate: 0.1,
        maskAllInputs: true,
        maskTextSelector: "*",
        blockSelector: "img,picture,video,audio,canvas,svg,.ph-no-record",
        recordHeaders: false,
        recordBody: false,
      },
      enable_recording_console_log: false,
      capture_performance: import.meta.env.DEV
        ? false
        : { network_timing: true, web_vitals: true },
      capture_exceptions: import.meta.env.DEV
        ? false
        : {
          capture_unhandled_errors: true,
          capture_unhandled_rejections: true,
          capture_console_errors: true,
        },
      before_send: (event) =>
        // Broad stages require the durable, receipt-gated editor transport.
        // Never queue them through the SDK or the legacy narrow sanitizer.
        event?.event === "starter_customization_started" ||
        (event?.properties.definition_version === "starter-customization-v1" &&
          (event.event === "starter_opened" || event.event === "starter_customization_saved"))
          ? null
          : errorDiagnostics.enrich(suppressKnownException(sanitizeLengthValueCapture(sanitizeOAuthCapture(redactAgentDraftCapture(event))))),
      loaded: (ph) => {
        if (import.meta.env.DEV) ph.opt_out_capturing();
      },
    });
    initialised = true;
  }
  return Promise.resolve();
}

/** Observe a PostHog multivariate flag without ever treating a missing flag as
 * an experiment assignment. Only editor callers should record exposures;
 * informational pricing surfaces may read the same flag without exposure. */
export function subscribeFeatureFlag(
  key: string,
  callback: (value: boolean | string | undefined) => void,
  captureExposure: boolean | ((value: boolean | string | undefined) => boolean) = true,
): () => void {
  let cancelled = false;
  let unsubscribe: (() => void) | undefined;
  let requestGeneration = 0;
  let refreshing = true;
  let subject: string | null = null;
  const publish = () => {
    if (cancelled || refreshing || subject !== getAnalyticsDistinctId()) return;
    // Read first without exposure: unresolved, opted-out and ineligible users
    // must never enter the native experiment denominator.
    if (!subject || posthog.has_opted_out_capturing()) return callback(undefined);
    const value = posthog.getFeatureFlag(key, { send_event: false, fresh: true });
    const expose = typeof captureExposure === "function" ? captureExposure(value) : captureExposure;
    if (expose && value !== undefined) {
      posthog.getFeatureFlag(key, { send_event: true, fresh: true });
    }
    callback(value);
  };
  const refresh = async () => {
    const generation = ++requestGeneration;
    subject = getAnalyticsDistinctId();
    refreshing = true;
    callback(undefined);
    // A callback alone is insufficient: identify() leaves cached flags in the
    // SDK and an older in-flight response may finish after the ID changed.
    // This public promise waits for OUR queued request, and SDK reset cancels
    // old requests. Never turn cached anonymous flags into signed-in exposure.
    const result = await posthog.featureFlags?.reloadFeatureFlagsAsync();
    if (cancelled || generation !== requestGeneration || subject !== getAnalyticsDistinctId()) return;
    refreshing = false;
    if (result?.status === "loaded") publish();
    else callback(undefined);
  };
  void initAnalytics().then(() => {
    if (cancelled || !initialised) return;
    featureFlagIdentityListeners.add(refresh);
    unsubscribe = posthog.onFeatureFlags((_flags, _variants, context) => {
      if (cancelled || refreshing) return;
      if (context?.errorsLoading === true) callback(undefined);
      else if (context?.errorsLoading === false) publish();
    });
    void refresh();
  });
  return () => {
    cancelled = true;
    featureFlagIdentityListeners.delete(refresh);
    unsubscribe?.();
  };
}

const featureFlagIdentityListeners = new Set<() => Promise<void>>();

export type TrackOptions = {
  /** Send now over `sendBeacon` instead of the batched queue. For an event
   * captured a frame before a full navigation: the queue's unload flush is
   * best-effort, and a lost click reads as a visitor who never clicked. */
  instant?: boolean;
};

/** Generic event API retained for upstream product/export/AI telemetry. */
export function track(
  event: string,
  props?: Record<string, unknown>,
  options?: TrackOptions,
): void {
  if (typeof window !== "undefined" && /^\/handover\/?$/.test(window.location.pathname)) return;
  try { trackMetaProductEvent(event, props); } catch { /* Advertising must never interrupt the editor. */ }
  if (!initialised) return;
  errorDiagnostics.record(event, props);
  if (options?.instant) {
    posthog.capture(event, props, {
      send_instantly: true,
      transport: "sendBeacon",
    });
    return;
  }
  posthog.capture(event, props);
}

export function captureException(
  error: unknown,
  properties?: Record<string, unknown>,
): void {
  if (!initialised) return;
  posthog.captureException(error, properties);
}

/** Force recording when somebody opens a feedback surface, while keeping the
 * normal background recording rate at 10%. All text, inputs and media remain
 * masked by the global recorder configuration. */
export function startFeedbackRecording(): void {
  if (!initialised || import.meta.env.DEV) return;
  posthog.startSessionRecording({ sampling: true });
}

export function getFeedbackTelemetryContext(): {
  distinctId: string | null;
  sessionId: string | null;
  replayUrl: string | null;
} {
  if (!initialised) {
    return { distinctId: null, sessionId: null, replayUrl: null };
  }
  return {
    distinctId: posthog.get_distinct_id() || null,
    sessionId: posthog.get_session_id() || null,
    replayUrl: posthog.get_session_replay_url({
      withTimestamp: true,
      timestampLookBack: 60,
    }) || null,
  };
}

/** AI requests may link metrics to the existing session, but never enable
 * analytics or bypass an opt-out on the server. No replay URL or user content. */
export function getAiTelemetryContext(): {
  enabled: boolean;
  distinctId: string | null;
  sessionId: string | null;
} {
  const disabled = { enabled: false, distinctId: null, sessionId: null };
  try {
    if (import.meta.env.DEV ||
      (typeof window !== "undefined" && /^\/handover\/?$/.test(window.location.pathname)) ||
      !isAnalyticsCapturing()) return disabled;
    return {
      enabled: true,
      distinctId: posthog.get_distinct_id() || null,
      sessionId: posthog.get_session_id() || null,
    };
  } catch {
    // Broken or blocked telemetry must not prevent a paid generation.
    return disabled;
  }
}

/** Used only in memory to deduplicate experiment exposures after identify/reset;
 * never attach the raw ID to our custom experiment event. */
export function isAnalyticsCapturing(): boolean {
  return initialised && !posthog.has_opted_out_capturing();
}

/** Read the existing initialized destination; never initialize a second SDK.
 * A mutated SDK configuration must not route durable events elsewhere. */
export function getAnalyticsTransportConfig(): { apiHost: string; projectToken: string } | null {
  if (!isAnalyticsCapturing() || !KEY || posthog.config.token !== KEY || posthog.config.api_host !== HOST) return null;
  return { apiHost: HOST, projectToken: KEY };
}

export function getAnalyticsDistinctId(): string | null {
  return initialised ? posthog.get_distinct_id() || null : null;
}

export function identify(
  distinctId: string,
  props?: Record<string, unknown>,
): void {
  if (!initialised) return;
  // Auth owns the rich identify call. The billing bootstrap's compatibility
  // call carries no person properties and must not duplicate it.
  if (
    identifiedDistinctId === distinctId &&
    (!props || Object.keys(props).length === 0)
  ) {
    return;
  }
  const previousId = getAnalyticsDistinctId();
  posthog.identify(distinctId, props);
  identifiedDistinctId = distinctId;
  if (previousId !== getAnalyticsDistinctId()) {
    for (const refresh of featureFlagIdentityListeners) void refresh();
  }
}

export function resetAnalyticsIdentity(): void {
  if (!initialised) return;
  errorDiagnostics.clear();
  posthog.reset();
  identifiedDistinctId = null;
  for (const refresh of featureFlagIdentityListeners) void refresh();
}

export function registerSuperProperties(
  props: Record<string, unknown>,
): void {
  if (!initialised) return;
  posthog.register(props);
}

/** Refresh attribution after boot. First-value properties are unknown when the
 * initial pageview is captured, so update both future events and the current
 * identified person when that milestone actually happens. */
export function refreshAttributionProperties(
  props: Record<string, unknown>,
): void {
  if (!initialised) return;
  posthog.register(props);
  if (identifiedDistinctId !== null) posthog.setPersonProperties(props);
}

/** Compatibility names used by the billing bootstrap. */
export function identifyAnalytics(userId: string): void {
  identify(userId);
}

export function resetAnalytics(): void {
  // An anonymous boot must not mint a fresh anonymous id. Auth already resets
  // a known identity synchronously on sign-out, so this also avoids a second
  // reset from the billing effect observing the same transition.
  if (identifiedDistinctId === null) return;
  resetAnalyticsIdentity();
}

export function captureAnalytics<E extends keyof AnalyticsEvents>(
  event: E,
  properties: AnalyticsEvents[E],
): void {
  track(event, sanitizeAnalyticsProperties(properties));
}

/** One id joins a paywall impression to both client checkout milestones and
 * the verified webhook completion persisted with the purchase intent. */
export function createCheckoutAttemptId(): string {
  return globalThis.crypto.randomUUID();
}

const BLOCKED_PROPERTY =
  /(^|_)(email|project_name|project_data|content|body|notes?)($|_)/i;

export function sanitizeAnalyticsProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties).filter(
      ([key]) => !BLOCKED_PROPERTY.test(key),
    ),
  );
}

/** Automatic events may run between a same-document navigation and hashchange.
 * Strip draft fragments from every URL-shaped string, including autocaptured
 * href attributes, referrers and nested initial/person properties. */
export function redactAgentDraftCapture(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event;
  const seen = new WeakMap<object, unknown>();
  const redact = (value: unknown): unknown => {
    if (typeof value === "string") {
      const clean = redactEmailTrackingUrls(value);
      const draftIndex = clean.search(/(?:#|%23)agent-draft/i);
      // Owner-side link autocapture and replay also see bearer links. Fragments
      // must never leave through automatic properties or nested DOM attributes.
      const handover = /(?:\/|%2f)handover(?:\/|%2f)?(?:#|%23)/i.exec(clean);
      const handoverIndex = handover ? handover.index + handover[0].search(/#|%23/i) : -1;
      const indices = [draftIndex, handoverIndex].filter(index => index >= 0);
      return indices.length ? clean.slice(0, Math.min(...indices)) : clean;
    }
    if (!value || typeof value !== "object" || value instanceof Date) return value;
    if (seen.has(value)) return seen.get(value);
    if (Array.isArray(value)) {
      const items: unknown[] = [];
      seen.set(value, items);
      for (const item of value) items.push(redact(item));
      return items;
    }
    const result: Record<string, unknown> = {};
    seen.set(value, result);
    for (const [key, item] of Object.entries(value)) {
      Object.defineProperty(result, key, { value: redact(item), enumerable: true, configurable: true, writable: true });
    }
    return result;
  };
  return redact(event) as CaptureResult;
}

const OAUTH_EVENTS = new Set([
  "auth_oauth_initiated", "auth_oauth_returned", "auth_oauth_succeeded", "auth_oauth_failed",
]);
const OAUTH_URL_PROPERTIES = new Set([
  "$current_url", "$referrer", "$initial_current_url", "$initial_referrer",
  "$session_entry_url", "$session_entry_referrer",
]);

/** OAuth callback credentials can appear in URLs added by the SDK after track().
 * Cover event and nested person properties while retaining browser/UA context. */
export function sanitizeOAuthCapture(event: CaptureResult | null): CaptureResult | null {
  if (!event || !OAUTH_EVENTS.has(event.event)) return event;
  const seen = new WeakMap<object, unknown>();
  const sanitize = (value: unknown): unknown => {
    if (!value || typeof value !== "object" || value instanceof Date) return value;
    if (seen.has(value)) return seen.get(value);
    if (Array.isArray(value)) {
      const items: unknown[] = [];
      seen.set(value, items);
      for (const item of value) items.push(sanitize(item));
      return items;
    }
    const result: Record<string, unknown> = {};
    seen.set(value, result);
    for (const [key, item] of Object.entries(value)) {
      if (OAUTH_URL_PROPERTIES.has(key) && typeof item !== "string") continue;
      const clean = OAUTH_URL_PROPERTIES.has(key)
        ? (item as string).split(/[?#]|%3f|%23/i, 1)[0]
        : sanitize(item);
      Object.defineProperty(result, key, { value: clean, enumerable: true, configurable: true, writable: true });
    }
    return result;
  };
  return sanitize(event) as CaptureResult;
}

// Applied after the SDK merges its automatic and registered properties. Merely
// sanitizing our five custom fields would still send token-bearing URLs and
// arbitrary first-touch campaign/referrer text with this new event.
const LENGTH_VALUE_PROPERTIES = new Set([
  "token", // SDK collection key, required by ingestion; never a user credential.
  "experiment_key", "experiment_version", "variant", "assignment_id", "assigned_at", "surface",
  "$feature/equipment-cap-free-build-v1",
  "starter_source", "eligibility", "eligibility_reason",
  "status", "equipment_count", "cable_count", "assumption_count", "finding_count",
  "definition_version", "edit_kind", "result_kind", "saved_revision", "$insert_id",
  "distinct_id", "$device_id", "$session_id", "$window_id", "$lib", "$lib_version",
  "$host", "$pathname", "$browser", "$browser_version", "$os", "$os_version",
  "$device_type", "$browser_language", "$browser_language_prefix", "$is_identified",
  "$process_person_profile", "arrival_surface", "arrival_at", "first_value_action",
  "first_value_at", "first_value_was_ai",
]);

// Only these starter/value events need the native traffic classifier input.
// Do not widen the shared allowlist for offers, equipment or agent-draft events.
const STARTER_TRAFFIC_EVENTS = new Set([
  "starter_opened", "starter_customization_saved",
  "meaningful_project_value_reached", "meaningful_project_value_eligibility",
]);

export function sanitizeLengthValueCapture(event: CaptureResult | null): CaptureResult | null {
  if (!event || !["meaningful_project_value_reached", "meaningful_project_value_eligibility",
    "starter_customization_saved", "starter_opened", "agent_draft_opened", "agent_draft_reviewed",
    "agent_draft_imported", "equipment_cap_experiment_exposed", "offer_experiment_assigned",
    "offer_experiment_exposed", "offer_assignment_conflict"].includes(event.event)) return event;
  const agentDraft = ["agent_draft_opened", "agent_draft_reviewed", "agent_draft_imported"].includes(event.event);
  return { uuid: event.uuid, event: event.event, timestamp: event.timestamp,
    properties: Object.fromEntries(Object.entries(event.properties).filter(
    ([key, value]) => key === "$raw_user_agent"
      ? STARTER_TRAFFIC_EVENTS.has(event.event) && isValidAnalyticsUserAgent(value)
      : key === "search_project_theme"
      ? ["starter_opened", "starter_customization_saved"].includes(event.event) &&
        event.properties.starter_source === "resource_example" && isSearchProjectTheme(value)
      : key === "transport"
      ? agentDraft && typeof value === "string" && ["mcp", "http", "unknown"].includes(value)
      : LENGTH_VALUE_PROPERTIES.has(key) && (value === null || ["string", "number", "boolean"].includes(typeof value)),
  )) };
}

export function hasReachedValueMoment(system: {
  nodes: unknown[];
  cables: unknown[];
}): boolean {
  return system.nodes.length >= 2 && system.cables.length >= 1;
}
